import styled from '@emotion/styled'
import { Autocomplete, Paper, Stack, TextField } from '@mui/material'
import axios from 'axios'
import StoreCountry from 'GlobalStores/Cores/County/StoreCountry'
import { T, useTranslate } from 'GlobalStores/Cores/Translator/useTranslate'
import { storeElement } from 'GlobalStores/StoreElement'
import { runInAction } from 'mobx'
import { observer } from 'mobx-react-lite'
import { useRef, useState } from 'react'
import HttpHelper from 'Services/HttpHelper'
import DefaultInput from '../DefaultInput'
import { DefaultLabelTypo } from '../InputsStyled'
import { get } from 'lodash'
import specialCountryData from './specialCountryData.json'

const CustomPaper = props => (
    <Paper
        {...props}
        sx={{
            marginY: '16px',
            border: '1px solid #F1F5F8',
            '.MuiAutocomplete-listbox': {
                padding: '0px',
                '.MuiAutocomplete-option': {
                    padding: '16px 16px',
                    fontFamily: 'Inter, NotoSansThai',
                    fontWeight: 400,
                    fontSize: '12px',
                    color: 'var(--input-text)'
                },
                '.Mui-focused': {
                    backgroundColor: '#ebf2f9 !important'
                }
            }
        }}
    />
)

const AutoAddress = observer(({ itemKeys, item = {}, inputCallback, selectedAddressCallback, onBlur }) => {
    const translate = useTranslate()

    const [autoAddressStates, setAutoAddressStates] = useState({
        searchValue: '',
        selectedValue: null,
        options: []
    })
    const [addressId, setAddressId] = useState('')
    const autoCompleteInputRef = useRef()
    const [doneSearch, setDoneSearch] = useState(false)

    //only for AUS and NZL
    const autoSearchSpace = ['AUS', 'NZL'].includes(StoreCountry.Country3())

    const GetAddress = async values => {
        //logic to get address
        const getAddressLogic = () => {
            if (autoSearchSpace) {
                return values.endsWith(' ')
            } else {
                return values.length > 4 && addressId === ''
            }
        }

        if (getAddressLogic()) {
            setDoneSearch(false)

            // setTimeout(() => {}, 1000)
            const country3 = StoreCountry.Country3()
            const isSpecialCountry = ['JPN'].includes(country3)
            const specialCountry = isSpecialCountry ? specialCountryData.find(item => item.key === country3) : null
            let country = !!specialCountry ? get(specialCountry, 'value', country3) : country3

            const valueSearch = values.split(' ').join('+')
            const url = `https://international-autocomplete.api.smarty.com/v2/lookup?search=${valueSearch}&country=${country}&key=116245232163030886`
            // const url = `https://international-autocomplete.api.smartystreets.com/lookup?key=116245232163030886&search=${values}&country=${country}`
            let temp = []
            try {
                const response = await axios.get(HttpHelper.Proxy({ originalUrl: url }))

                response.data.candidates.map(candidate => {
                    temp.push({
                        value: candidate.address_text,
                        data: candidate.address_id
                    })
                })

                setAutoAddressStates({
                    ...autoAddressStates,
                    searchValue: values,
                    options: temp
                })
            } catch (e) {
                if (e?.response?.data?.errors) {
                    inputCallback(values)
                }
            }
        }
    }

    const commitLeaf = candidate => {
        inputCallback(candidate.street)
        selectedAddressCallback({ data: candidate })
        setDoneSearch(true)
        setAutoAddressStates({
            searchValue: candidate.street || '',
            selectedValue: candidate,
            options: []
        })
    }

    const candidateToOption = candidate => {
        if (candidate.street) {
            const label = itemKeys.map(key => candidate[key]).filter(Boolean).join(' ').trim()
            return { value: label || candidate.street, data: candidate }
        }
        return { value: candidate.address_text, data: candidate.address_id }
    }

    const GetAddressByAddressId = async address_id => {
        if (!address_id) return

        const country3 = StoreCountry.Country3()
        const isSpecialCountry = ['JPN'].includes(country3)
        const specialCountry = isSpecialCountry ? specialCountryData.find(item => item.key === country3) : null
        let country = !!specialCountry ? get(specialCountry, 'value', country3) : country3

        const url = `https://international-autocomplete.api.smarty.com/v2/lookup/${address_id}?country=${country}&key=116245232163030886&max_results=10`

        try {
            const response = await axios.get(url)
            const candidates = response.data?.candidates || []

            // Single fully-resolved leaf → auto-commit (THA-style 2-step flow).
            if (candidates.length === 1 && candidates[0].street) {
                commitLeaf(candidates[0])
                return
            }

            // Otherwise present in dropdown; user picks then either drills again or commits.
            setAutoAddressStates({
                ...autoAddressStates,
                options: candidates.map(candidateToOption)
            })
        } catch (err) {
            console.error(err)
        }
    }

    return (
        <AutoAddressStyled>
            <div
                style={{
                    display: 'flex',
                    justifyContent: storeElement.dynamicWidth > 768 && 'space-between',
                    marginBottom: '8px',
                    paddingLeft: '16px',
                    display: (autoAddressStates.selectedValue === null && !item.value) || autoSearchSpace ? 'block' : 'none'
                }}>
                <label>
                    <DefaultLabelTypo variant="label">
                        <T>{item.label}</T>
                    </DefaultLabelTypo>
                </label>
            </div>
            <Autocomplete
                style={{ width: '100%', display: (autoAddressStates.selectedValue === null && !item.value) || autoSearchSpace ? 'inherit' : 'none' }}
                id="custom-input-demo"
                freeSolo
                disableCloseOnSelect
                options={autoAddressStates.options}
                filterOptions={x => x}
                getOptionLabel={option => (typeof option === 'string' ? option : option.value || '')}
                renderInput={params => (
                    <TextField
                        fullWidth
                        type="text"
                        inputProps={{
                            ...params.InputProps,
                            'aria-autocomplete': 'none',
                            spellCheck: 'false'
                        }}
                        InputProps={{
                            ...params.InputProps,
                            'aria-autocomplete': 'none',
                            spellCheck: 'false'
                        }}
                        {...params}
                        placeholder=" "
                        onChange={e => {
                            setAutoAddressStates({
                                ...autoAddressStates,
                                searchValue: e.target.value
                            })
                            GetAddress(e.target.value)
                            if (e.target.value.length === 0) {
                                setAddressId('')
                                setAutoAddressStates({
                                    options: [],
                                    selectedValue: null,
                                    searchValue: ''
                                })
                            }
                        }}
                        autoCorrect="off"
                        inputRef={autoCompleteInputRef}
                        onBlur={e => {
                            if (autoAddressStates.searchValue.length === 0 && !autoAddressStates.selectedValue) {
                                setAutoAddressStates({
                                    ...autoAddressStates,
                                    selectedValue: null,
                                    searchValue: ''
                                })
                                inputCallback('')
                            } else {
                                if (autoAddressStates.selectedValue === null && autoAddressStates.searchValue.length > 0 && addressId === '') {
                                    inputCallback(e.target.value)
                                }
                            }
                        }}
                    />
                )}
                fullWidth
                open={doneSearch === false && autoAddressStates.options.length > 0}
                value={item.value || null}
                onChange={(e, newValue) => {
                    if (doneSearch === true) return
                    if (!newValue || newValue.data === undefined || newValue.data === null) return

                    // If `data` is a string, it's an address_id that needs another drill.
                    // If `data` is an object with `street`, it's the fully-resolved leaf → commit.
                    if (typeof newValue.data === 'string') {
                        setAddressId(newValue.data)
                        GetAddressByAddressId(newValue.data)
                    } else if (newValue.data.street) {
                        commitLeaf(newValue.data)
                    }

                    runInAction(() => {
                        storeElement.scrollTo = true
                    })
                }}
                isOptionEqualToValue={(option, value) => {
                    // (option && option.value && value ? option.value === value.value : '')
                    if (option && value) {
                        return option.value === value.value
                    } else {
                        return ''
                    }
                }}
                disableClearable
                sx={autoCompleteStyle}
                PaperComponent={CustomPaper}
            />
            <div style={{ display: (autoAddressStates.selectedValue !== null || item.value) && !autoSearchSpace ? 'inherit' : 'none' }}>
                <DefaultInput
                    label={translate(item.label)}
                    value={item.value}
                    onChange={e => {
                        if (e.target.value.length === 0) {
                            setAddressId('')
                            setAutoAddressStates({
                                // ...autoAddressStates,
                                options: [],
                                selectedValue: null,
                                searchValue: ''
                            })
                            inputCallback('')
                            if (autoCompleteInputRef.current) {
                                setTimeout(() => {
                                    autoCompleteInputRef.current.focus()
                                }, 500)
                            }
                        } else {
                            inputCallback(e.target.value)
                        }
                    }}
                />
            </div>
        </AutoAddressStyled>
    )
})

const autoCompleteStyle = {
    '.MuiOutlinedInput-root': {
        borderRadius: '8px',
        color: 'var(--input-text)',
        fontWeight: 400,
        fontSize: 16,
        height: '48px',
        padding: '0 16px',
        backgroundColor: 'var(--input-bg)',
        // filled state: กล่องทั้งใบเปลี่ยนสีเมื่อ input ข้างในมีค่า
        '&:has(input:not(:placeholder-shown))': {
            backgroundColor: 'var(--input-filled-bg)'
        },
        '.MuiAutocomplete-input': {
            padding: 0
        },
        '&:hover': {
            border: '0px!important'
        },
        '&.Mui-focused': {
            border: 0,
            '.MuiOutlinedInput-notchedOutline': {
                border: '1px solid var(--input-focus)'
            }
        }
    },
    '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
        border: '1px solid var(--input-border)',
        '&:hover': {
            border: '0px!important'
        }
    },
    '& .Mui-focused': {
        border: 0,
        '.MuiOutlinedInput-notchedOutline': {
            border: '1px solid var(--input-focus)'
        }
    },
    ' .MuiInputBase-root': {
        '&:hover': {
            border: '0px!important',
            '.MuiOutlinedInput-notchedOutline': {
                border: '1px solid var(--input-border)'
            }
        }
    }
}

const AutoAddressStyled = styled(Stack)``
export default AutoAddress
